import type { DatabaseRow } from '../types/database.types'

export class SchemaValidationError extends Error {
  public constructor(message: string) {
    super(message)
    this.name = 'SchemaValidationError'
  }
}

export function assertObject(
  value: unknown,
  label: string,
): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new SchemaValidationError(`${label} must be an object.`)
  }

  return value as Record<string, unknown>
}

export function assertKnownKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
  label: string,
): void {
  const unknownKeys = Object.keys(value).filter(
    (key) => !allowedKeys.includes(key),
  )

  if (unknownKeys.length > 0) {
    throw new SchemaValidationError(
      `${label} contains columns not present in the verified schema: ${unknownKeys.join(', ')}.`,
    )
  }
}

export function toDatabaseRow(
  value: unknown,
  allowedKeys: readonly string[],
  label: string,
): DatabaseRow {
  const row = assertObject(value, label)
  assertKnownKeys(row, allowedKeys, label)

  return Object.fromEntries(
    Object.entries(row).filter(
      ([, fieldValue]) =>
        fieldValue === null ||
        typeof fieldValue === 'string' ||
        typeof fieldValue === 'number' ||
        typeof fieldValue === 'boolean',
    ),
  )
}
